import { useMemo } from 'react';
import {
  sortAssets,
  useAccountsRests,
  useAccountsStore,
  type AssetRests,
} from '@/entities/account';
import { useAssetsIcons } from '@/shared/api/queries/useAssetsIconsDictionary';
import { DEFAULT_BASE_ASSET, NUMERIC_CONFIG } from '@/shared/config';
import {
  applyAmountPrecision,
  applyPricePrecision,
  formatReceivedAmount,
  formatReceivedPrice,
  useAppSettingsStore,
} from '@/shared/lib';
import type { WalletAssetRow } from './types';

const DEFAULT_BASE_LOW_BALANCE = 10;

const useWalletsAssetRows = () => {
  const { ewallet } = useAccountsStore();
  const { data: accountsRests, isLoading: isAccountsRestsLoading } =
    useAccountsRests();
  const { assetsIconsData } = useAssetsIcons();
  const { isLowBalancesHidden } = useAppSettingsStore();

  const mainAccount = ewallet?.accounts[0] ?? null;
  const baseCode = ewallet?.baseCode ?? DEFAULT_BASE_ASSET;
  const basePrecission =
    ewallet?.basePrecission ?? NUMERIC_CONFIG.precision.default;
  const baseLowBalance =
    accountsRests?.ewallet?.[0]?.baseLowBalance ?? DEFAULT_BASE_LOW_BALANCE;

  const rows: Array<WalletAssetRow> = useMemo(() => {
    if (!mainAccount) {
      return [];
    }

    const sortedAssets = sortAssets([...mainAccount.assets]) as AssetRests[];

    return sortedAssets.map(asset => {
      const precision = asset.precision ? asset.precision : basePrecission;

      const amount = applyAmountPrecision(
        asset.available + asset.reserved,
        precision,
        'credit'
      );
      const baseAmount = applyPricePrecision(
        (asset.baseAvailable ?? 0) + (asset.baseReserved ?? 0),
        basePrecission
      );
      const reserved = applyAmountPrecision(
        asset.reserved,
        precision,
        'credit'
      );
      const baseReserved = applyPricePrecision(
        asset.baseReserved ?? 0,
        basePrecission
      );

      return {
        code: asset.code,
        name: asset.name,
        icon: assetsIconsData?.value?.[asset.code],
        group: asset.group ?? undefined,
        amount,
        amountText: formatReceivedAmount(amount),
        baseAmount,
        baseAmountText: formatReceivedPrice(baseAmount),
        reserved,
        reservedText: formatReceivedAmount(reserved),
        baseReserved,
        baseReservedText: formatReceivedPrice(baseReserved),
      };
    });
  }, [mainAccount, basePrecission, assetsIconsData]);

  const visibleRows = useMemo(() => {
    if (!isLowBalancesHidden) {
      return rows;
    }

    return rows.filter(row => row.baseAmount >= baseLowBalance);
  }, [rows, isLowBalancesHidden, baseLowBalance]);

  return {
    rows: visibleRows,
    hasAssets: rows.length > 0,
    mainAccount,
    baseCode,
    basePrecission,
    baseLowBalance,
    isLoading: !ewallet || isAccountsRestsLoading,
  };
};

export { useWalletsAssetRows };
